import { Button, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ReactElement, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCanAccess, useNotify } from 'react-admin';
import { useReportClickReferrerCreate } from '../../../../DataProvider';
import type { ClicksByReferrersResource } from '../../../../Entities/ClicksByReferrer';
import { getClicksByReferrerReportParams } from './utils';
import { getReportDateLabels, type ReportFilter } from '../../../../Components/ReportsWidget/utils';

export interface PreviewButtonProps {
  onPreviewStart: (labels: { startDateLabel: string; endDateLabel: string }) => void;
  onPreviewSuccess: (records: ClicksByReferrersResource[]) => void;
  onPreviewError: () => void;
  autoPreviewOnMount?: boolean;
}

export default function PreviewButton({
  onPreviewStart,
  onPreviewSuccess,
  onPreviewError,
  autoPreviewOnMount = false,
}: PreviewButtonProps): ReactElement | null {
  const { handleSubmit } = useFormContext();
  const notify = useNotify();
  const [runReport, report] = useReportClickReferrerCreate();
  const hasAutoPreviewTriggered = useRef(false);

  const reportsCreateAccess = useCanAccess({ resource: 'reports', action: 'create' });
  const clicksByReferrersListAccess = useCanAccess({
    resource: 'clicks_by_referrers',
    action: 'list',
  });

  const handlePreview = handleSubmit(async (values) => {
    if (report.isPending) {
      return;
    }

    const filter = values as ReportFilter;
    const { startDate, endDate } = getReportDateLabels(filter);
    onPreviewStart({ startDateLabel: startDate, endDateLabel: endDate });

    try {
      const result = await runReport(getClicksByReferrerReportParams(filter));
      onPreviewSuccess(result.data);
    } catch (error) {
      onPreviewError();
      notify(error instanceof Error ? error.message : 'Preview failed', { type: 'error' });
    }
  });

  useEffect(() => {
    if (
      !autoPreviewOnMount ||
      hasAutoPreviewTriggered.current ||
      !reportsCreateAccess.canAccess ||
      !clicksByReferrersListAccess.canAccess
    ) {
      return;
    }

    hasAutoPreviewTriggered.current = true;
    void handlePreview();
  }, [
    autoPreviewOnMount,
    clicksByReferrersListAccess.canAccess,
    handlePreview,
    reportsCreateAccess.canAccess,
  ]);

  if (!reportsCreateAccess.canAccess || !clicksByReferrersListAccess.canAccess) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      onClick={() => void handlePreview()}
      disabled={report.isPending}
      startIcon={
        report.isPending ? <CircularProgress size={16} /> : <VisibilityIcon fontSize="small" />
      }
    >
      {report.isPending ? 'Loading…' : 'Preview'}
    </Button>
  );
}
